import { z } from 'zod';
import { LIMITS } from './project.js';
/**
 * Fase 09 — Vídeo avanzado (extensions/phase-09-10).
 *
 * Contratos del storyboard, los planos (Shot), los takes, la revisión humana
 * y el presupuesto. Todo lo que el navegador envía pasa por aquí; los valores
 * de proveedor (duraciones, resoluciones, precios) son los **documentados** y
 * llevan su fecha: no se inventan parámetros (06_DECISIONS_APPROVAL, P09).
 */
export const ADVANCED_VIDEO_LIMITS = {
    totalDurationMinMs: 8_000,
    totalDurationMaxMs: 60_000,
    shotMinCount: 2,
    shotMaxCount: 8,
    lockedTraitsMax: 20,
    referencesPerShotMax: 3,
    takesPerShotWithoutApproval: 2,
    promptMaxLength: 2_000,
};
/** Proveedor de vídeo aprobado (P09): Google Veo 3.1 vía Gemini API. */
export const VIDEO_PROVIDER = 'google';
export const videoModelSchema = z.enum([
    'veo-3.1-fast-generate-preview',
    'veo-3.1-generate-preview',
    'veo-3.1-lite-generate-preview',
]);
/** Duraciones admitidas por Veo 3.1 (ai.google.dev/gemini-api/docs/veo, 2026-10-07). */
export const VEO_DURATIONS_MS = [4_000, 6_000, 8_000];
/** Con referencias, extensión, 1080p o 4K la duración debe ser 8 s. */
export const VEO_DURATION_WITH_REFERENCES_MS = 8_000;
export const videoResolutionSchema = z.enum(['720p', '1080p']);
/**
 * Precio por segundo generado, USD, con audio (que Veo siempre genera).
 * Fuente: ai.google.dev/gemini-api/docs/pricing, leído el 2026-10-07. Es la
 * tarifa publicada: sirve para estimar y reservar; **no** es el coste observado,
 * que la API no devuelve. Solo se cobra si el vídeo se genera con éxito.
 */
export const VEO_PRICE_PER_SECOND_USD = {
    'veo-3.1-generate-preview': { '720p': '0.40', '1080p': '0.40' },
    'veo-3.1-fast-generate-preview': { '720p': '0.10', '1080p': '0.12' },
    'veo-3.1-lite-generate-preview': { '720p': '0.05', '1080p': '0.08' },
};
export const VEO_PRICE_SOURCE = {
    url: 'https://ai.google.dev/gemini-api/docs/pricing',
    checkedAt: '2026-10-07',
    currency: 'USD',
};
/**
 * Duración que se pide al proveedor: la menor admitida que cubre la pedida,
 * porque el renderer recorta pero nunca estira. Por encima del máximo, el máximo.
 */
export function providerDurationFor(requestedMs, withReferences) {
    if (withReferences)
        return VEO_DURATION_WITH_REFERENCES_MS;
    return VEO_DURATIONS_MS.find((d) => d >= requestedMs) ?? VEO_DURATIONS_MS[VEO_DURATIONS_MS.length - 1];
}
/** Coste estimado de un clip, como string decimal (sin flotantes). */
export function estimateClipCostUsd(model, resolution, durationMs) {
    const porSegundo = VEO_PRICE_PER_SECOND_USD[model][resolution];
    // Precio en milésimas de dólar para operar con enteros.
    const milesimas = Math.round(Number(porSegundo) * 1000);
    const total = (milesimas * durationMs) / 1000; // milésimas × segundos
    return (total / 1000).toFixed(4);
}
// --- Referencias y continuidad ----------------------------------------------
export const referenceRoleSchema = z.enum(['subject', 'style', 'location', 'product', 'start_frame', 'end_frame']);
export const continuityStrategySchema = z.enum(['REFERENCES', 'START_FRAME', 'PREVIOUS_FRAME']);
export const continuityReferenceSchema = z.object({
    assetId: z.uuid(),
    role: referenceRoleSchema,
});
export const continuityProfileInputSchema = z.object({
    expectedRevision: z.number().int().min(1),
    strategy: continuityStrategySchema.default('REFERENCES'),
    lockedTraits: z.array(z.string().trim().min(1).max(500)).min(1).max(ADVANCED_VIDEO_LIMITS.lockedTraitsMax),
    references: z.array(continuityReferenceSchema).max(ADVANCED_VIDEO_LIMITS.referencesPerShotMax),
});
export const continuityProfileSchema = z.object({
    id: z.uuid(),
    revision: z.number().int(),
    strategy: continuityStrategySchema,
    lockedTraits: z.array(z.string()),
    references: z.array(continuityReferenceSchema),
    approvedAt: z.string().nullable(),
});
// --- Storyboard (salida del agente) -----------------------------------------
export const STORYBOARD_SCHEMA_VERSION = 1;
export const storyboardShotSchema = z.object({
    position: z.number().int().min(0).max(ADVANCED_VIDEO_LIMITS.shotMaxCount - 1),
    title: z.string().min(1).max(LIMITS.sceneTitleMaxLength),
    /** Descripción editorial en el idioma del usuario (va a la Scene). */
    description: z.string().min(1).max(LIMITS.sceneDescriptionMaxLength),
    /** Acción, cámara y entorno: en inglés, para el proveedor. */
    action: z.string().min(1).max(2_000),
    camera: z.string().min(1).max(1_000),
    environment: z.string().min(1).max(2_000),
    requestedDurationMs: z.number().int().min(LIMITS.sceneDurationMinMs).max(LIMITS.sceneDurationMaxMs),
    narrationText: z.string().max(LIMITS.sceneTextMaxLength).nullable(),
    continuityParentPosition: z.number().int().min(0).max(ADVANCED_VIDEO_LIMITS.shotMaxCount - 2).nullable(),
});
export const storyboardPlanSchema = z.object({
    schemaVersion: z.literal(STORYBOARD_SCHEMA_VERSION),
    projectType: z.enum(['ADVANCED_VIDEO', 'VIRTUAL_CREATOR']),
    targetDurationMs: z.number().int().min(ADVANCED_VIDEO_LIMITS.totalDurationMinMs).max(ADVANCED_VIDEO_LIMITS.totalDurationMaxMs),
    aspectRatio: z.enum(['16:9', '9:16']),
    concept: z.string().min(1).max(2_000),
    /** Estilo común para los prompts de vídeo e imágenes de arranque (inglés). */
    visualStyle: z.string().min(1).max(LIMITS.styleMaxLength),
    lockedTraits: z.array(z.string().min(1).max(500)).min(1).max(ADVANCED_VIDEO_LIMITS.lockedTraitsMax),
    musicPrompt: z.string().min(1).max(LIMITS.musicPromptMaxLength),
    shots: z.array(storyboardShotSchema).min(ADVANCED_VIDEO_LIMITS.shotMinCount).max(ADVANCED_VIDEO_LIMITS.shotMaxCount),
});
// --- Shots, takes y revisión (API) ------------------------------------------
export const shotStatusSchema = z.enum(['DRAFT', 'GENERATING', 'NEEDS_REVIEW', 'REVIEWED']);
export const takeStatusSchema = z.enum(['PENDING', 'RUNNING', 'COMPLETED', 'FAILED', 'CANCELLED']);
export const reviewDecisionSchema = z.enum(['PASS', 'REJECT']);
export const checkResultSchema = z.enum(['PASS', 'REJECT', 'NOT_APPLICABLE']);
export const REVIEW_CHECKS = ['identity', 'wardrobe', 'location', 'lighting', 'motion', 'product', 'quality'];
export const reviewInputSchema = z.object({
    checks: z.object(Object.fromEntries(REVIEW_CHECKS.map((c) => [c, checkResultSchema]))),
    decision: reviewDecisionSchema,
    notes: z.string().trim().max(2_000).default(''),
});
export const continuityReviewSchema = z.object({
    id: z.uuid(),
    checks: z.record(z.string(), checkResultSchema),
    decision: reviewDecisionSchema,
    notes: z.string(),
    reviewedAt: z.string(),
});
export const shotTakeSchema = z.object({
    id: z.uuid(),
    generationId: z.uuid(),
    status: takeStatusSchema,
    outputAssetId: z.uuid().nullable(),
    contentUrl: z.string().nullable(),
    inputSnapshot: z.object({
        model: videoModelSchema,
        resolution: videoResolutionSchema,
        durationMs: z.number().int(),
        aspectRatio: z.enum(['16:9', '9:16']),
        prompt: z.string(),
        references: z.array(continuityReferenceSchema),
        estimatedCostUsd: z.string(),
    }),
    technicalError: z.unknown().nullable(),
    reviews: z.array(continuityReviewSchema),
    createdAt: z.string(),
    completedAt: z.string().nullable(),
});
export const shotSchema = z.object({
    id: z.uuid(),
    sceneId: z.uuid(),
    position: z.number().int(),
    title: z.string(),
    description: z.string(),
    prompt: z.string(),
    action: z.string(),
    camera: z.string(),
    environment: z.string(),
    requestedDurationMs: z.number().int(),
    providerDurationMs: z.number().int(),
    continuityParentShotId: z.uuid().nullable(),
    activeTakeId: z.uuid().nullable(),
    status: shotStatusSchema,
    takes: z.array(shotTakeSchema),
});
export const createShotRequestSchema = z.object({
    expectedRevision: z.number().int().min(1),
    title: z.string().trim().min(1).max(LIMITS.sceneTitleMaxLength),
    description: z.string().trim().min(1).max(LIMITS.sceneDescriptionMaxLength),
    action: z.string().trim().min(1).max(2_000),
    camera: z.string().trim().min(1).max(1_000),
    environment: z.string().trim().min(1).max(2_000),
    requestedDurationMs: z.number().int().min(LIMITS.sceneDurationMinMs).max(LIMITS.sceneDurationMaxMs),
    continuityParentShotId: z.uuid().nullable().default(null),
});
export const updateShotRequestSchema = createShotRequestSchema.partial().extend({
    expectedRevision: z.number().int().min(1),
    /** Prompt técnico editable a mano; si no se envía se regenera desde acción/cámara/entorno. */
    prompt: z.string().trim().min(1).max(ADVANCED_VIDEO_LIMITS.promptMaxLength).optional(),
});
export const generateShotRequestSchema = z.object({
    expectedRevision: z.number().int().min(1),
    model: videoModelSchema.optional(),
    resolution: videoResolutionSchema.default('720p'),
});
export const shotEstimateSchema = z.object({
    model: videoModelSchema,
    resolution: videoResolutionSchema,
    durationMs: z.number().int(),
    references: z.array(continuityReferenceSchema),
    estimatedCostUsd: z.string(),
    pricePerSecondUsd: z.string(),
    priceSource: z.object({ url: z.string(), checkedAt: z.string(), currency: z.string() }),
    /** Lo que queda disponible en cada tope que aplica. */
    budget: z.array(z.object({
        scope: z.enum(['PROJECT', 'OWNER_MONTHLY']),
        limitUsd: z.string(),
        committedUsd: z.string(),
        reservedUsd: z.string(),
        availableUsd: z.string(),
        fits: z.boolean(),
    })),
    takesSoFar: z.number().int(),
    takesWithoutApproval: z.number().int(),
    allowed: z.boolean(),
    reason: z.string().nullable(),
});
export const activeTakeRequestSchema = z.object({
    expectedRevision: z.number().int().min(1),
    takeId: z.uuid(),
});
export const projectCostsSchema = z.object({
    currency: z.literal('USD'),
    policies: z.array(z.object({
        scope: z.enum(['PROJECT', 'OWNER_MONTHLY']),
        limitUsd: z.string(),
        committedUsd: z.string(),
        reservedUsd: z.string(),
        unknownCount: z.number().int(),
        availableUsd: z.string(),
    })),
    entries: z.array(z.object({
        jobId: z.uuid(),
        kind: z.enum(['ESTIMATE', 'ESTIMATED_SETTLEMENT', 'OBSERVED', 'UNKNOWN', 'RELEASE']),
        amountUsd: z.string().nullable(),
        createdAt: z.string(),
    })),
});
// --- Límites de gasto aprobados -----------------------------------------------
/**
 * Topes registrados en extensions/phase-09-10/docs/06_DECISIONS_APPROVAL.md
 * («ok a todo», 2026-10-07). USD. Cambiarlos exige una nueva aprobación.
 */
export const APPROVED_BUDGET = {
    approvedAt: '2026-10-10',
    evidence: 'Conversación 2026-10-10: 5 USD/proyecto, 2 takes para pruebas, 1 take en producción con regeneración individual. Clips de 4 s sin recorte.',
    currency: 'USD',
    /** Tope por proyecto según el modelo de vídeo. */
    perProjectUsd: {
        'veo-3.1-fast-generate-preview': '5.0000',
        'veo-3.1-lite-generate-preview': '5.0000',
        'veo-3.1-generate-preview': '30.0000',
    },
    /** Tope mensual global de las fases 09 y 10, todos los modelos. */
    ownerMonthlyUsd: '100.0000',
    takesPerShotWithoutApproval: ADVANCED_VIDEO_LIMITS.takesPerShotWithoutApproval,
    concurrentVideoGenerations: 1,
    smokeMaxClips: 2,
    smokeMaxImages: 6,
};
/** Aritmética decimal exacta en diezmilésimas (4 decimales), sin flotantes. */
export function toMicroUsd(valor) {
    const match = /^(-?)(\d+)(?:\.(\d{1,4}))?$/.exec(valor.trim());
    if (!match)
        throw new Error(`Importe inválido: ${valor}`);
    const [, signo, entero, decimales = ''] = match;
    const base = BigInt(entero) * 10000n + BigInt((decimales + '0000').slice(0, 4));
    return signo === '-' ? -base : base;
}
export function fromMicroUsd(valor) {
    const negativo = valor < 0n;
    const abs = negativo ? -valor : valor;
    const entero = abs / 10000n;
    const decimales = (abs % 10000n).toString().padStart(4, '0');
    return `${negativo ? '-' : ''}${entero}.${decimales}`;
}
//# sourceMappingURL=video.js.map